import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Trophy, Github, ExternalLink, Globe, Youtube, Figma, Users, Calendar,
} from 'lucide-react';
import projects, { getProjectById } from '../data/projects';
import Footer from '../components/Footer';
import Magnetic from '../components/Magnetic';
import TextReveal from '../components/TextReveal';
import { chipPop, staggerContainer, EASE_OUT } from '../lib/motion';
import './ProjectDetail.css';

const linkMeta = {
  github: { label: 'GitHub', icon: Github },
  demo: { label: 'Live demo', icon: ExternalLink },
  website: { label: 'Website', icon: Globe },
  video: { label: 'Video', icon: Youtube },
  figma: { label: 'Figma', icon: Figma },
  devpost: { label: 'Devpost', icon: ExternalLink },
};

const ProjectDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const project = getProjectById(slug);
  const [heroFailed, setHeroFailed] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!project) {
    return (
      <main className="project-detail project-detail--missing">
        <div className="container">
          <h1>Project not found</h1>
          <p>That project doesn't exist (or moved).</p>
          <Link to="/projects" className="btn btn-primary">Back to all projects</Link>
        </div>
      </main>
    );
  }

  const others = projects.filter((p) => p.id !== project.id).slice(0, 3);
  const gallery = project.gallery && project.gallery.length ? project.gallery : [];
  const heroImg = !heroFailed ? project.image : null;

  return (
    <main className="project-detail">
      <div className="container">
        <Magnetic strength={0.2}>
          <button className="project-detail__back" onClick={() => navigate(-1)}>
            <ArrowLeft size={18} /> Back
          </button>
        </Magnetic>

        <motion.header
          className="project-detail__header"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {(project.award || project.launched) && (
            <div className="project-detail__badges">
              {project.award && (
                <span className="award-badge"><Trophy size={13} /> {project.award}</span>
              )}
              {project.launched && (
                <span className="live-badge">Live now{project.links.website ? ` at ${project.links.website.replace(/^https?:\/\//, '')}` : ''}</span>
              )}
            </div>
          )}
          <TextReveal as="h1" className="project-detail__title" text={project.title} mode="words" delay={0.1} />
          <p className="project-detail__tagline">{project.tagline}</p>

          <div className="project-detail__meta">
            <span><Calendar size={15} /> {project.hackathon} · {project.year}</span>
            <span>
              <Users size={15} />
              {project.solo ? 'Solo build' : `With ${project.team.join(', ')}`}
            </span>
          </div>

          <div className="project-detail__links">
            {Object.entries(project.links).map(([key, url]) => {
              if (!url || !linkMeta[key]) return null;
              const { label, icon: Icon } = linkMeta[key];
              return (
                <a key={key} href={url} target="_blank" rel="noreferrer" className="btn btn-ghost project-detail__link">
                  <Icon size={16} /> {label}
                </a>
              );
            })}
          </div>
        </motion.header>

        {heroImg && (
          <motion.div
            className="project-detail__hero"
            initial={{ opacity: 0, scale: 1.08, clipPath: 'inset(6% round 16px)' }}
            animate={{ opacity: 1, scale: 1, clipPath: 'inset(0% round 16px)' }}
            transition={{ duration: 0.7, delay: 0.15, ease: EASE_OUT }}
          >
            <img src={heroImg} alt={project.title} onError={() => setHeroFailed(true)} />
          </motion.div>
        )}

        <div className="project-detail__body">
          <div className="project-detail__content">
            {project.sections.map((s) => (
              <section className="project-detail__section" key={s.title}>
                <h2>{s.title}</h2>
                <p>{s.body}</p>
              </section>
            ))}
          </div>

          <aside className="project-detail__aside">
            <div className="project-detail__panel glass gradient-border">
              <h3>Built with</h3>
              <motion.div
                className="project-detail__chips"
                variants={staggerContainer(0.04)}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: '-40px' }}
              >
                {project.tech.map((t) => (
                  <motion.span className="chip" key={t} variants={chipPop}>{t}</motion.span>
                ))}
              </motion.div>
            </div>
            {!project.solo && (
              <div className="project-detail__panel glass">
                <h3>Team</h3>
                <ul className="project-detail__team">
                  <li>Shaik Hatim</li>
                  {project.team.map((m) => <li key={m}>{m}</li>)}
                </ul>
              </div>
            )}
          </aside>
        </div>

        {gallery.length > 1 && (
          <div className="project-detail__gallery">
            <h2>Gallery</h2>
            <div className="project-detail__gallery-grid">
              {gallery.map((src, i) => (
                <a key={i} href={src} target="_blank" rel="noreferrer" className="project-detail__shot">
                  <img src={src} alt={`${project.title} screenshot ${i + 1}`} loading="lazy" />
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="project-detail__more">
          <h2>More projects</h2>
          <div className="project-detail__more-grid">
            {others.map((p) => (
              <button key={p.id} className="project-detail__more-card" onClick={() => navigate(`/project/${p.id}`)}>
                <span className="project-detail__more-name">{p.title}</span>
                <span className="project-detail__more-tag">{p.hackathon}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <Footer />
    </main>
  );
};

export default ProjectDetail;
